// ─────────────────────────────────────────────────────────────────────────────
// WP-08 — Piezas transversales de la Reportería de Reconocimiento: estados honestos
// (loading/error/empty), fila de KPIs, listas rankeadas con respeto de supresión por
// anonimato y la escala de madurez por Score (0-1) reutilizada por Heatmap y Red ONA.
// ─────────────────────────────────────────────────────────────────────────────
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getRecognitionErrorMessage } from '@/api/recognition';
import type { RankedEntryDto, CampaignDto, RecognitionReportParams } from '@/api/recognition';
import type { UserRole } from '@/hooks/useCurrentUser';
import { luminancia, mezclarConBlanco } from '@/utils/colorMix';
import { useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { useOUItems } from '@/hooks/useOUs';
import { alcanceNivelMasAlto } from './campanasReporte';
import type { FiltroDeTira } from './AvisoFiltroNoAplica';
import { Sparkline } from './Sparkline';

/**
 * C7 (ronda de revisión, 2026-08-13) — roles con acceso al Mapa de Calor OU × Fundamental.
 *
 * `AdminGlobal` y `AdminBrillamos` reciben el MISMO trato en las 12 secciones de Reconocimiento
 * (`src/auth/accessMatrix.ts` → `RECOGNITION_DIRECT_ACCESS`, incluida `reporteria`) — no hay
 * precedente en este módulo de separarlos. El backend confirma esa lectura: la capacidad
 * `MapaDeCalor` (`CapabilityPolicy.cs`, SharedContracts) exige `ViewAnalytics` + `PermissionScope.
 * Global` SIN `RestrictedToRoles`, y `AdminBrillamos` tiene `MaxScope: Global` igual que
 * `AdminGlobal` (`RoleCatalog.cs`) — un `AdminBrillamos` real recibe 200, no 403.
 *
 * `AdminOUBrillamos` (`MaxScope: OuBu`) SÍ queda fuera — su 403 está confirmado, no es la misma
 * duda. No se gatea con `orPermission="viewAnalytics"` (el patrón de `TableroView`/`TeamView` para
 * capacidades `NoRoleRestriction`) porque ese permiso está oculto del editor de roles de
 * Reconocimiento (`AdminRoleEditorModal.tsx`) y no está confirmado que hoy pueda asignarse a
 * `AdminOUBrillamos` — usarlo arriesgaría sobre-otorgar el Heatmap a ese rol.
 *
 * Constante ÚNICA para que `RecognitionAnalyticsView` (el `RoleGuard` que envuelve `HeatmapTab`) y
 * `DemograficoExportButton` (qué rol dispara la query del Heatmap para el export) no puedan volver
 * a desalinearse — antes cada uno tenía su propia lista literal.
 */
export const HEATMAP_ALLOWED_ROLES: UserRole[] = ['AdminGlobal', 'AdminBrillamos'];

/**
 * Estado canónico de una campaña para el REPORTE GLOBAL (`CampaignsGlobalExportButton`,
 * `historicosExcel.ts`): activo / expirado / borrador. Su valor es la clave de
 * `reports.history.status.*` y decide el color del texto de la columna «Estado» del Excel.
 *
 * Vivía en `HistoryTab.tsx` (la pestaña «Históricos» vieja, eliminada el 2026-09-29; ver
 * `docs/contexto/historicos-eliminado-rollback.md`) y se reubicó aquí antes de borrarla, porque el
 * botón del reporte global la importaba de allí.
 *
 * Tuvo una gemela, `campaignDisplayStatus` (valor final `finalizada` en vez de `expirado`), que se
 * eliminó el 2026-09-29 junto con sus dos consumidores: `CampaignRowActions` y la columna «Estado» de
 * Campañas General. `estadoVisible` se queda porque el Excel indexa `reports.history.status.*`.
 */
export function estadoVisible(estado: CampaignDto['estado']): 'activo' | 'expirado' | 'borrador' {
  if (estado === 'Finalizada') return 'expirado';
  if (estado === 'Borrador') return 'borrador';
  return 'activo';
}

interface ReportStatusProps {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  isEmpty: boolean;
  onRetry?: () => void;
  children: ReactNode;
}

/** Muestra el estado real de una consulta (loading/error/empty) o el contenido. Nunca mocks. */
export function ReportStatus({ isLoading, isError, error, isEmpty, onRetry, children }: Readonly<ReportStatusProps>) {
  const { t } = useTranslation('recognition');
  if (isLoading) {
    return (
      <div className="rr-state">
        <i className="fas fa-spinner fa-spin" aria-hidden="true" /> {t('reports.common.loading')}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="rr-state rr-state--error" role="alert">
        <i className="fas fa-triangle-exclamation" aria-hidden="true" />
        <span>{getRecognitionErrorMessage(error, t('reports.common.error'))}</span>
        {onRetry && (
          <button type="button" className="rr-retry" onClick={onRetry}>
            {t('reports.common.retry')}
          </button>
        )}
      </div>
    );
  }
  if (isEmpty) {
    return (
      <div className="rr-state">
        <i className="fas fa-inbox" aria-hidden="true" /> {t('reports.common.empty')}
      </div>
    );
  }
  return <>{children}</>;
}

/** Aviso de que algún agregado fue suprimido por umbral de anonimato (<5). */
/**
 * UAT 17-ago-2026 — la nota decía QUÉ pasó («algunos agregados se ocultaron por el umbral de
 * anonimato») pero no POR QUÉ, y se leyó como un fallo de la pantalla: «esto del umbral de anonimato
 * me parece que no debería aplicar… de todos modos debería haber datos aquí y no hay nada».
 *
 * Es la regla RN-V05 de la Especificación (sección Visibilidad, Privacidad y GDPR) funcionando como
 * debe. La segunda línea explica a quién protege y deja dicho lo que faltaba: que no es un error ni
 * un hueco de datos. Se pinta visible y no como tooltip a propósito — quien necesita esta frase es
 * justamente quien no sabe que hay algo sobre lo que pasar el cursor.
 */
export function SuppressedNote() {
  const { t } = useTranslation('recognition');
  return (
    <div className="rr-suppressed-note">
      <div>
        <i className="fas fa-user-shield" aria-hidden="true" /> {t('reports.common.suppressedNote')}
      </div>
      <div className="rr-suppressed-note-help">{t('reports.common.suppressedNoteHelp')}</div>
    </div>
  );
}

/** Cuántas unidades se listan antes de resumir el resto en «+N». */
const ALCANCE_VISIBLE = 2;

/**
 * UAT 17-ago-2026 — «Alcance»: a qué unidades organizativas se dirigió la campaña.
 *
 * Nace de una petición que había que corregir a medias: Adriana pidió que «Audiencia» mostrara las
 * OUs. No se puede — la hoja «Definiciones» del cliente define Audiencia como el NÚMERO de empleados
 * y es el denominador de la tasa de participación, así que convertirla en lista rompería el
 * porcentaje. Lo que sí faltaba es esto: la pregunta «¿a quién se dirigió?» no tenía columna. Van
 * las dos, cada una con su unidad en el título.
 *
 * Alcance vacío NO es «ninguna OU»: en el modelo de campañas significa que llega a toda la
 * organización, y así lo lee el backend (ver `AudienceScope` en GetRecognitionCampaignSummary, donde
 * un alcance vacío cede al scope de quien consulta). Pintar «—» invitaría a leerlo como un hueco.
 *
 * 2026-09-24 (BJ-H4, REPORTERIA-REGLAS.md §8.3) — se muestra truncado al NIVEL MÁS ALTO elegido:
 * se eligió Europa y el árbol metió sus hijas ⇒ «Europa»; se eligió solo Dirección Perú ⇒ ese
 * nivel. Antes listaba cada nodo del subárbol y el «+N» escondía justamente lo que importaba.
 */
export function AlcanceCell({ alcance: alcanceCompleto }: Readonly<{ alcance: readonly string[] }>) {
  const { t } = useTranslation('recognition');
  const { resolveOuName } = useOuNameResolver();
  const { data: ouItems = [] } = useOUItems({ activeOnly: false, rootsOnly: false });
  const alcance = alcanceNivelMasAlto(alcanceCompleto, ouItems);

  if (alcance.length === 0) {
    return <span className="rr-muted">{t('reports.common.scopeAll')}</span>;
  }

  const nombres = alcance.map(resolveOuName);
  const visibles = nombres.slice(0, ALCANCE_VISIBLE);
  const ocultas = nombres.length - visibles.length;

  // El `title` lleva SIEMPRE la lista completa: el «+N» resume, no esconde.
  return (
    <span title={nombres.join(' · ')}>
      {visibles.join(', ')}
      {ocultas > 0 && <span className="rr-muted"> {t('reports.common.scopeMore', { n: ocultas })}</span>}
    </span>
  );
}

export interface KpiItem {
  label: string;
  value: ReactNode;
  color?: string;
  /**
   * T-11 — un valor por día del periodo para dibujar la tendencia bajo el número. Opcional: las
   * tarjetas de las demás pestañas no tienen serie y siguen pintándose igual que antes.
   */
  serie?: readonly number[];
  /** Descripción de la serie para lectores de pantalla. Obligatoria si viene `serie`. */
  serieAria?: string;
  /**
   * Línea secundaria bajo el número, como la maqueta (`fund-kpi` de `fundamentales-admin.js`): ahí
   * es donde vive la comparación contra el periodo anterior («↑ +12% vs. sem. ant.»).
   *
   * 🔴 Por qué existe: la comparación NO va en tarjetas aparte. Con «Periodo anterior» y
   * «Variación» como tarjetas propias la fila crecía a 6, y esas dos —al no ser series— quedaban
   * sin sparkline al lado de cuatro que sí la tienen. En UAT (24-ago) eso se reportó literalmente
   * como «faltan 2 gráficos»: dos huecos en medio de la fila se leen como algo roto, no como una
   * decisión. Metiendo la comparación dentro de la tarjeta el dato no se pierde y no hay hueco.
   */
  sub?: ReactNode;
  /**
   * Tono semántico de la línea secundaria: sube / baja / sin cambio. Es un TONO y no un color porque
   * el hex inline está prohibido en este árbol (`noInlineStyleRegression`) y la convención del repo
   * pide variables CSS — y porque los estados son tres y cerrados, no un color arbitrario.
   */
  subTone?: 'up' | 'down' | 'flat' | 'muted';
}

/**
 * Fila de KPIs (reutiliza las clases ona2-* del AnalyticsView). `className` agrega una variante de la
 * rejilla, p. ej. `rr-kpis--5` (2026-10-26, fila principal del Diario con «Reconocimientos grupales»).
 */
export function KpiRow({ items, className }: Readonly<{ items: KpiItem[]; className?: string }>) {
  return (
    <div className={className ? `ona2-kpis ${className}` : 'ona2-kpis'}>
      {items.map((k) => (
        <div key={k.label} className="ona2-kpi">
          <div className="ona2-kpi-label">{k.label}</div>
          <div className="ona2-kpi-value" style={{ color: k.color }}>{k.value}</div>
          {k.sub != null && (
            <div className={`rr-kpi-sub rr-kpi-sub--${k.subTone ?? 'flat'}`}>{k.sub}</div>
          )}
          {/* T-11 — la curvita de la maqueta. Sin serie la tarjeta queda exactamente como estaba. */}
          {k.serie && (
            <Sparkline valores={k.serie} color={k.color ?? 'currentColor'} aria={k.serieAria ?? k.label} />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Lista rankeada (top personas / fundamentales / campaña). Las filas `suprimido` se muestran
 * enmascaradas ("—") en vez de exponer el conteo, respetando el umbral de anonimato.
 *
 * RF-148 — con `total`, cada fila muestra además su % sobre ese total ("% Fundamental": cuánto
 * representa cada fundamental del total de reconocimientos del periodo). Sin `total`, el
 * comportamiento es idéntico al de antes de RF-148.
 */
export function RankedList({ entries, total }: Readonly<{ entries: RankedEntryDto[]; total?: number }>) {
  const { t } = useTranslation('recognition');
  if (entries.length === 0) {
    return <div className="rr-state">{t('reports.common.empty')}</div>;
  }
  return (
    <div className="rr-rank">
      {entries.map((e, i) => (
        <div key={e.id || i} className="rr-rank-item">
          <span className="rr-rank-num">{i + 1}</span>
          <span className="rr-rank-name">
            {e.suprimido ? <span className="rr-muted">{t('reports.common.masked')}</span> : e.nombre}
            {!e.suprimido && e.etiqueta ? <span className="rr-rank-tag"> · {e.etiqueta}</span> : null}
          </span>
          <span className="rr-rank-count">
            {e.suprimido ? '—' : e.count}
            {!e.suprimido && total ? (
              <span className="rr-muted"> ({Math.round((e.count / total) * 100)}%)</span>
            ) : null}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Formatea la variación porcentual con signo (ej: +12.5% / -4.0%). */
export function formatVariation(pct: number): string {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

/** Color de la variación (verde si sube, rojo si baja, gris si es 0). */
export function variationColor(pct: number): string {
  if (pct > 0) return '#059669';
  if (pct < 0) return '#dc2626';
  return '#6b7280';
}

/** Tono de la variación para `KpiItem.subTone`. Mismo criterio que `variationColor`, sin hex. */
export function variationTone(pct: number): 'up' | 'down' | 'flat' {
  if (pct > 0) return 'up';
  if (pct < 0) return 'down';
  return 'flat';
}

/** Piso de intensidad para una celda CON dato: nunca blanca pura, siempre algo que colorear. */
export const INTENSIDAD_MINIMA_CON_DATO = 0.12;

/** Piso de intensidad para una celda SIN dato: casi invisible, pero con un asomo del color de su
 *  columna — para que se note de qué Fundamental es aun sin reconocimientos que colorear. */
export const INTENSIDAD_MINIMA_SIN_DATO = 0.05;

/**
 * Estilo de una celda tintada con el color de SU columna (Fundamental), con intensidad
 * proporcional a `ratio` (0-1, ya normalizado contra el máximo de la columna). `piso` fija el
 * mínimo de intensidad — distinto para una celda con dato real (`INTENSIDAD_MINIMA_CON_DATO`) que
 * para una sin dato (`INTENSIDAD_MINIMA_SIN_DATO`, casi invisible mostrando la celda vacía).
 */
export function fundamentalTintStyle(
  colorBase: string, ratio: number, piso: number = INTENSIDAD_MINIMA_CON_DATO,
): { background: string; color: string } {
  const intensidad = Math.min(1, Math.max(piso, ratio));
  const mezcla = mezclarConBlanco(colorBase, intensidad);
  return {
    background: `rgb(${mezcla[0]}, ${mezcla[1]}, ${mezcla[2]})`,
    color: luminancia(mezcla) < 0.55 ? '#ffffff' : '#1f2937',
  };
}

/**
 * Reemplaza `nombre` en cada entrada por el nombre resuelto (persona u OU); conserva el resto del
 * DTO intacto. AnalyticsService solo devuelve IDs en los rankings de Reportería (nunca lee la BD de
 * otro microservicio) — la resolución es 100% frontend, igual que ya hace `useAnalyticsNames` para
 * el resto de AnalyticsView.
 *
 * Genérico sobre `{ id, nombre }` (no solo `RankedEntryDto`) para que rankings con forma propia
 * (ej. `ShineRankEntryDto`, RF-149) puedan reutilizarlo sin perder sus campos adicionales.
 */
export function withResolvedNames<T extends { id: string; nombre: string }>(
  entries: T[],
  resolveName: (id: string) => string,
): T[] {
  return entries.map((entry) => ({ ...entry, nombre: resolveName(entry.id) }));
}

/** Filtros de la tira que están puestos ahora mismo. De aquí sale qué avisa cada sección. */
export function filtrosActivosDe(
  params: Pick<RecognitionReportParams, 'fundamentalId' | 'personaId'>,
): FiltroDeTira[] {
  const activos: FiltroDeTira[] = [];
  if (params.fundamentalId) activos.push('fundamental');
  if (params.personaId) activos.push('persona');
  return activos;
}

/** Solo la persona: hay secciones que sí aceptan Fundamental y no aceptan persona (el embudo). */
export function soloPersonaDe(filtrosActivos: readonly FiltroDeTira[]): FiltroDeTira[] {
  return filtrosActivos.filter((filtro) => filtro === 'persona');
}

/**
 * Textos con los que un exportador dice «no hay OU elegida»: el guion largo de `SIN_DATO` que usan
 * los Excel de la plantilla teal, y la cadena vacía. No se importa `SIN_DATO` de `excelPlantillaTeal`
 * a propósito: esta pieza es transversal a la Reportería y no debe depender de un módulo de Excel.
 */
const SIN_OU_ELEGIDA = new Set(['—', '']);

/**
 * Punto 6 (revisión final, 2026-09-03) — IMPORTANTE: «Filtros aplicados» de los cuatro Excel de
 * Reportería (`buildResumenEjecutivoSheet`, `DailyTab.tsx`/`ShineTab.tsx`/`LeadersTab.tsx`/
 * `DemograficoExportButton.tsx`) solo llevaba la OU — con Fundamental o persona puestos en la tira,
 * el libro exportado declaraba por escrito que el único filtro fue la OU.
 *
 * Añade Fundamental/persona ACTIVOS a la línea que cada exportador ya arma para la OU, con el
 * NOMBRE resuelto (nunca el id crudo). Sin ninguno de los dos activos, el resultado es idéntico al
 * texto de OU de siempre — no cambia ningún Excel que hoy no tenga Fundamental ni persona puestos.
 *
 * QA Brillamos Juntos (2026-09-17) — sin OU elegida, `ShineTab`/`LeadersTab`/
 * `DemograficoExportButton` pasan el guion de «sin dato» como texto de OU, y la celda salía con ese
 * guion y el separador colgando delante del filtro real: «— · Fundamental: Actitudes y
 * Comportamientos». Una OU ausente no es un filtro que declarar, así que el guion se omite cuando
 * hay algún filtro de tira que sí declarar. Se conserva cuando no hay ninguno: ahí la celda sigue
 * diciendo, correctamente, que no se aplicó filtro alguno. `DailyTab` no entra por este camino —
 * pasa «Toda la organización», un texto con significado propio.
 */
export function conFiltrosDeTira(
  ouTexto: string,
  params: Pick<RecognitionReportParams, 'fundamentalId' | 'personaId'>,
  nombreDeFundamental: (id: string) => string,
  nombreDePersona: (id: string) => string,
  rotulos: EtiquetasDeFiltro = ROTULOS_EN_ESPANOL,
): string {
  const extra: string[] = [];
  if (params.fundamentalId) {
    extra.push(`${rotulos.fundamental}: ${nombreDeFundamental(params.fundamentalId)}`);
  }
  if (params.personaId) extra.push(`${rotulos.persona}: ${nombreDePersona(params.personaId)}`);
  if (extra.length === 0) return ouTexto;

  const ouDeclarable = SIN_OU_ELEGIDA.has(ouTexto.trim()) ? [] : [ouTexto];
  return [...ouDeclarable, ...extra].join(' · ');
}

/**
 * Rótulos de los dos filtros de la tira, para que el Excel que los declara pueda hacerlo en el
 * idioma de quien exporta (`reports.filters.fundamental` / `reports.filters.person`).
 */
export interface EtiquetasDeFiltro {
  fundamental: string;
  persona: string;
}

/**
 * Por omisión se conservan en español: los otros tres Excel que llaman a esta función siguen
 * generándose en español entero, y traducir solo estas dos palabras les dejaría una línea a medio
 * idioma. Cada uno los pasará traducidos cuando se localice su libro completo.
 */
const ROTULOS_EN_ESPANOL: EtiquetasDeFiltro = { fundamental: 'Fundamental', persona: 'Persona' };
